import { Component, inject, OnInit } from '@angular/core';
import { Subscription, takeUntil, timer } from 'rxjs';
import { ModalButton, ModalService } from '../../services';
import { NgClass } from '@angular/common';
import { Button } from 'primeng/button';
import type { ButtonSeverity } from 'primeng/types/button';
import { UnsubscribeOnDestroy } from '../../classes';

@Component({
    selector: 'modal',
    templateUrl: './modal.component.html',
    styleUrls: ['./modal.component.scss'],
    standalone: true,
    imports: [NgClass, Button]
})
export class ModalComponent extends UnsubscribeOnDestroy implements OnInit {
    private modalService = inject(ModalService);
    content: string;
    modalType: string;
    showModal: boolean = false;
    buttons?: ModalButton[];
    header?: string;
    autodismissTimer?: Subscription;

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.modalService.modalObs
            .pipe(takeUntil(this.ngUnsubscribe$))
            .subscribe((options) => {
                this.content = options.content;
                this.modalType = options.type;
                this.showModal = true;
                this.buttons = options.buttons;
                this.header = options.header;

                if (options.autodismiss) {
                    this.autodismissTimer = timer(typeof options.autodismiss === "boolean" ? 5000 : options.autodismiss).subscribe((n) => this.hideModal());
                }
            });
    }

    hideModal(): void {
        if (!this.buttons) {
            if (this.autodismissTimer) {
                this.autodismissTimer.unsubscribe();
            }
            this.showModal = false;
            this.modalService.resultObs.next("hidden");
        }
    }

    buttonClick(result: any): void {
        if (this.autodismissTimer) {
            this.autodismissTimer.unsubscribe();
        }
        this.showModal = false;
        this.modalService.resultObs.next(result);
    }

    modalButtonSeverity(button: ModalButton): ButtonSeverity {
        if (button.severity) {
            return button.severity;
        }
        const c = button.cssClass ?? [];
        if (c.some((x) => x === 'btn-danger')) {
            return 'danger';
        }
        if (c.some((x) => x === 'btn-success')) {
            return 'success';
        }
        if (c.some((x) => x === 'btn-warning')) {
            return 'warn';
        }
        return 'primary';
    }

    modalButtonOutlined(button: ModalButton): boolean {
        if (button.outlined !== undefined) {
            return button.outlined;
        }
        return (button.cssClass ?? []).some((x) => x === 'btn-outline-secondary');
    }

    /** Host classes (layout only — never Bootstrap `btn` / `btn-*`). */
    modalButtonExtraHostClasses(button: ModalButton): string[] {
        return (button.cssClass ?? []).filter((cls) => cls !== 'btn' && !/^btn-/i.test(cls));
    }
}

